import { useCallback, useEffect, useState } from 'react';
import { loginOnAmazon, logoutOnAmazon } from '../../lib/amazon';
import { loginOnFacebook } from '../../lib/facebook';
import { loadAmazonLoginScript, loadFacebookLoginScript } from '../../lib/loadScript';
import { useUser } from '../useUser';
import { SocialAuthProvider } from '../useSocialAuth';
import { useConnectUserAccountMutation } from './useConnectUserAccountMutation';

const TOKEN_GETTERS = {
  [SocialAuthProvider.Amazon]: async () => (await loginOnAmazon()).access_token,
  [SocialAuthProvider.Facebook]: loginOnFacebook
};

export const useSocialAccountConnection = (provider: SocialAuthProvider) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { connectUser, disconnectUser } = useConnectUserAccountMutation();
  const { user, setUserData } = useUser();

  const handleConnect = useCallback(async () => {
    setLoading(true);
    try {
      const token = await TOKEN_GETTERS[provider]();
      const response = await connectUser(provider, token);
      setUserData({ ...user, ...response });
    } catch (error) {
      setError(error);
      throw error;
    }
    setLoading(false);
  }, [connectUser, provider, setUserData, user]);

  const handleDisconnect = useCallback(async () => {
    setLoading(true);
    try {
      const response = await disconnectUser(provider);
      await setUserData({ ...user, ...response });
      if (provider === SocialAuthProvider.Amazon) logoutOnAmazon();
    } catch (error) {
      setError(error);
      throw error;
    }
    setLoading(false);
  }, [disconnectUser, provider, setUserData, user]);

  useEffect(() => {
    switch (provider) {
      case SocialAuthProvider.Amazon:
        return loadAmazonLoginScript();
      case SocialAuthProvider.Facebook:
        return loadFacebookLoginScript();
    }
  }, [provider]);

  return {
    handleConnect,
    handleDisconnect,
    loading,
    error
  };
};
